import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Play, Pause, Loader2 } from 'lucide-react';
import { useMeditationAudioUrl } from '@/hooks/useMeditationAudioUrl';
import { useTokenAutoRefresh } from '@/hooks/useTokenAutoRefresh';

interface MinimalAudioPlayerProps {
  audioUrl: string;
  onEnded?: () => void;
  autoRefreshToken?: boolean;
  className?: string;
}

/**
 * Minimal audio player with just play/pause button and seeking bar
 * Used for quick audio preview in admin interfaces
 */
const MinimalAudioPlayer: React.FC<MinimalAudioPlayerProps> = ({
  audioUrl: rawAudioUrl,
  onEnded,
  autoRefreshToken = true,
  className = ''
}) => {
  // Convert to streaming URL with token
  const { streamingUrl: initialUrl, isLoading: isConverting, error } =
    useMeditationAudioUrl(rawAudioUrl);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const [, forceUpdate] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Auto-refresh token during playback (prevents interruption for long sessions)
  const { currentStreamingUrl: audioUrl } = useTokenAutoRefresh({
    streamingUrl: initialUrl,
    isPlaying,
    enabled: autoRefreshToken && !rawAudioUrl?.startsWith('blob:')
  });

  // Audio element event handlers - NUCLEAR FIX: Try EVERYTHING to get duration
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      // Also grab duration on every timeupdate
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    const updateDuration = () => {
      const newDuration = audio.duration;
      if (isFinite(newDuration) && newDuration > 0) {
        setDuration(newDuration);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onEnded?.();
    };

    const handleWaiting = () => setIsBuffering(true);

    const handleCanPlay = () => {
      setIsBuffering(false);
      // Try to get duration when canplay fires too
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    // Listen to ALL duration-related events
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('loadeddata', updateDuration); // Additional event
    audio.addEventListener('durationchange', updateDuration); // Additional event
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('canplaythrough', updateDuration); // Additional event
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('waiting', handleWaiting);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('loadeddata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('canplaythrough', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('waiting', handleWaiting);
    };
  }, [onEnded]);

  // Reset playback when URL changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && audioUrl) {
      // Cleanup force update interval if exists
      if ((audio as any)._forceUpdateInterval) {
        clearInterval((audio as any)._forceUpdateInterval);
      }

      setDuration(0); // Reset duration when loading new audio
      audio.load();
      setCurrentTime(0);
      setIsPlaying(false);

      // Poll for duration every 100ms for first 3 seconds
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = setInterval(() => {
        attempts++;
        if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
          clearInterval(pollInterval);
        } else if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
        }
      }, 100);

      return () => {
        clearInterval(pollInterval);
        if ((audio as any)._forceUpdateInterval) {
          clearInterval((audio as any)._forceUpdateInterval);
        }
      };
    }
  }, [audioUrl]);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        await audio.play();
        setIsPlaying(true);

        // Force updates every 100ms when playing (timeupdate event is unreliable)
        const forceUpdateInterval = setInterval(() => {
          if (audio.paused) {
            clearInterval(forceUpdateInterval);
            return;
          }
          setCurrentTime(audio.currentTime);
          if (isFinite(audio.duration) && audio.duration > 0) {
            setDuration(audio.duration);
          }
          forceUpdate(n => n + 1);
        }, 100);

        // Store interval ID for cleanup
        (audio as any)._forceUpdateInterval = forceUpdateInterval;
      } catch (err) {
        console.error('Playback failed:', err);
        setIsPlaying(false);
      }
    }
  };

  const handleSeek = (value: number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    // If duration state is 0 but audio element has duration, use it directly
    const effectiveDuration = duration > 0 ? duration : (isFinite(audio.duration) ? audio.duration : 0);

    if (effectiveDuration > 0 && audioUrl) {
      const clampedTime = Math.max(0, Math.min(value[0], effectiveDuration));
      audio.currentTime = clampedTime;
      setCurrentTime(clampedTime);

      // Update state duration if we found it
      if (duration === 0 && effectiveDuration > 0) {
        setDuration(effectiveDuration);
      }
    }
  };

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="text-sm text-destructive p-2 text-center">
        Failed to load audio
      </div>
    );
  }

  if (isConverting) {
    return (
      <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading audio...</span>
      </div>
    );
  }

  return (
    <div className={`w-full space-y-3 ${className}`}>
      <audio
        ref={audioRef}
        src={audioUrl || undefined}
        preload="auto"
        playsInline
        controlsList="nodownload"
      />

      {/* Play/Pause Button */}
      <div className="flex justify-center">
        <Button
          variant="outline"
          size="lg"
          onClick={togglePlayPause}
          disabled={!audioUrl}
          className="h-12 w-12 rounded-full"
        >
          {isBuffering ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5" />
          )}
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="space-y-1">
        <Slider
          value={[currentTime]}
          max={Math.max(duration, 0.1)}
          step={0.1}
          onValueChange={handleSeek}
          disabled={false}
          className="cursor-pointer"
          title={duration <= 0 ? 'Waiting for audio to load...' : `Duration: ${formatTime(duration)}`}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span className="font-mono">{formatTime(currentTime)}</span>
          <span className="font-mono">{formatTime(duration)}</span>
        </div>
        {/* Playback status */}
        {duration <= 0 && audioUrl && (
          <div className="text-xs text-yellow-600 font-mono">
            Loading audio...
          </div>
        )}
      </div>
    </div>
  );
};

export default MinimalAudioPlayer;
